<script lang="ts">
  import type { SvelteComponent } from 'svelte';
  import { AppState, SetNotification } from '../../etc/stores';
  import greetings from './messages/Greetings.svelte';
  import completeRegistration from './messages/CompleteRegistration.svelte';
  import youGotCoins from './messages/YouGotCoins.svelte';
  import provisionalCoins from './messages/ProvisionalCoins.svelte';
  import creditCardGotCoins from './messages/CreditCardGotCoins.svelte';
  import remindCheckout from './messages/RemindCheckout.svelte';
  import remindWelcomeCoin from './messages/RemindWelcomeCoin.svelte';
  import remindPostCheckout from './messages/RemindPostCheckout.svelte';
  import remindReferred from './messages/RemindReferred.svelte';
  import ReferredCoin from './messages/ReferredCoin.svelte';
  import ReferredAlreadyJoined from './messages/ReferredAlreadyJoined.svelte';
  import postCheckoutLoading from './messages/PostCheckoutLoading.svelte';
  import creditCardGotCoinsMobile from './messages/CreditCardGotCoinsMobile.svelte';
  import { isMobile, notificationAudio } from '../../etc/utils';
  import { Auth } from '../../services/auth';
  import { Container } from '../../services/container';
  import CollapsedIcon from '../icons/CollapsedIcon.svelte';
  import { fade, fly } from 'svelte/transition';
  import Toast from '../misc/Toast.svelte';
  import ChevronDown from '../icons/ChevronDown.svelte';
  import Heading from '../typography/Heading.svelte';
  import WelcomeCoinAvailable from './messages/WelcomeCoinAvailable.svelte';
  import WelcomeCoinPreviouslyClaimed from './messages/WelcomeCoinPreviouslyClaimed.svelte';
  import WelcomeCoinToNewCustomers from './messages/WelcomeCoinToNewCustomers.svelte';
  import WelcomeCoinExpired from './messages/WelcomeCoinExpired.svelte';
  import PaymnetCoinUnexpectedError from './messages/PaymentCoinUnexpectedError.svelte';
  import PaymentCoinSaved from './messages/PaymentCoinSaved.svelte';
  import ReferYourselfError from './messages/ReferYourselfError.svelte';
  import FastRedemption from './messages/FastRedemption.svelte';

  interface IMessage {
    component: typeof SvelteComponent;
    containerStyle: 'message' | 'popup' | 'custom';
    disableTimeoutToClose?: boolean;
    overlay?: boolean;
    hideArrow?: boolean;
    customStyle?: string;
  }

  type TMessage = typeof SvelteComponent | IMessage;

  const auth = Container.get(Auth);

  const positionSettings = $AppState.settings || {};

  $: category = $AppState.website.category;

  const animate = (node, args) => {
    const animations = {
      popup: fly(node, { y: 50 }),
      message: fly(node, { x: 50 }),
      custom: () => null
    };

    return animations[args.containerStyle];
  };

  const getNotificationStyle = () => {
    if (isMobile()) {
      const placement =
        positionSettings.position === 'bottomLeft' ? 'left' : 'right';
      const scale = auth.provider === 'wix' ? 0.8 : 1;
      return `
        ${placement}: 0;
        transform: scale(${scale});
        ${getFromMessage(messages[category], 'customStyle', '')}
      `;
    }
  };

  const getArrowStyle = () => {
    const placement =
      positionSettings.position === 'bottomLeft' ? 'left' : 'right';
    const spacing =
      auth.provider === 'wix' && isMobile()
        ? positionSettings.mobileSideSpacing * 1.4
        : positionSettings.mobileSideSpacing;
    return `
      ${placement}: ${spacing}px;
    `;
  };

  const popupConfig = (component: typeof SvelteComponent) =>
    ({
      component,
      containerStyle: 'popup',
      disableTimeoutToClose: true,
      overlay: true
    } as IMessage);

  const getFromMessage = (
    singleMessage: TMessage,
    key: keyof IMessage,
    defaultValue: any
  ) => {
    return typeof singleMessage === 'object'
      ? singleMessage[key]
      : defaultValue;
  };

  let direction = positionSettings.position || 'bottomRight';
  let prevCategory;
  let customStyles = '';
  let timeout = null;

  $: {
    const placement =
      positionSettings.position === 'bottomLeft' ? 'left' : 'right';
    const sideSpacing =
      (isMobile()
        ? positionSettings.mobileSideSpacing
        : positionSettings.desktopSideSpacing) ?? 30;
    const bottomSpacing =
      (isMobile()
        ? positionSettings.mobileBottomSpacing
        : positionSettings.desktopBottomSpacing) ?? 30;
    customStyles = [
      `${placement}: ${sideSpacing}px`,
      `bottom: ${bottomSpacing}px`
    ].join(';');
  }

  const defaultMessages = {
    welcomeCoinAvailable: popupConfig(WelcomeCoinAvailable),
    paymentCoinSaved: popupConfig(PaymentCoinSaved),
    welcomeCoinPreviouslyClaimed: popupConfig(WelcomeCoinPreviouslyClaimed),
    welcomeCoinToNewCustomers: popupConfig(WelcomeCoinToNewCustomers),
    welcomeCoinExpired: popupConfig(WelcomeCoinExpired),
    paymentCoinUnexpectedError: popupConfig(PaymnetCoinUnexpectedError),
    referYourselfError: popupConfig(ReferYourselfError),
    referredCoin: popupConfig(ReferredCoin),
    referredAlreadyJoined: popupConfig(ReferredAlreadyJoined),
    completeRegistration,
    remindCheckout,
    remindWelcomeCoin,
    remindReferred
  };

  /*
   * TO-DO: fix each of those messages to not show up when there is no loyalty enabled
   * so we can delete this conditional and simply add all messages at once into
   * the messages object
   */
  const messages: { [k: string]: TMessage } = $AppState.business.loyaltyEnabled
    ? {
        ...defaultMessages,
        greetings: {
          component: greetings,
          containerStyle: 'popup'
        },
        youGotCoins: popupConfig(youGotCoins),
        provisionalCoins: popupConfig(provisionalCoins),
        creditCardGotCoins,
        fastRedemption: {
          ...popupConfig(FastRedemption),
          hideArrow: true
        },
        creditCardGotCoinsMobile: {
          component: creditCardGotCoinsMobile,
          containerStyle: 'custom',
          disableTimeoutToClose: true
        },
        postCheckoutLoading: {
          component: postCheckoutLoading,
          containerStyle: 'custom',
          disableTimeoutToClose: true
        },
        remindPostCheckout: {
          component: remindPostCheckout,
          containerStyle: 'custom',
          disableTimeoutToClose: true
        }
      }
    : defaultMessages;

  $: containerStyle = getFromMessage(
    messages[category],
    'containerStyle',
    'message'
  );

  $: {
    if (category && category !== prevCategory) {
      clearTimeout(timeout);
      prevCategory = category;
      if (!getFromMessage(messages[category], 'disableTimeoutToClose', false)) {
        notificationAudio.play();
        timeout = setTimeout(() => {
          SetNotification('');
        }, 8000);
      }
    }
  }
</script>

<div
  class="twism-widget {auth.provider} twism-widget--{direction}"
  style={customStyles}
>
  {#if $AppState.website.showCollapsedIcon}
    <div
      class="
        twism-widget__container
        twism-widget__container--{direction}
        {$AppState.settings.widgetStyleText && !$AppState.website.popoverOpen
        ? 'twism-widget__container--text'
        : ''}
        {getFromMessage(messages[category], 'overlay', false)
        ? 'disable-click'
        : ''}
      "
      style={auth.provider === 'wix' && isMobile()
        ? `transform: scale(0.8);`
        : ''}
      on:touchstart
      on:click
      transition:fade|local
    >
      {#if $AppState.website.popoverOpen}
        <ChevronDown style="transform: translateY(2px);" />
      {:else}
        <CollapsedIcon />
        {#if $AppState.settings.widgetStyleText}
          <Heading
            class="twism-widget__collapsed-text"
            type="h3"
            color={$AppState.theme.colors.textAboveBackground}
          >
            {$AppState.settings.widgetStyleText}
          </Heading>
        {/if}
      {/if}
    </div>
  {/if}
  {#if category && messages[category]}
    {#if getFromMessage(messages[category], 'overlay', false) && isMobile()}
      <div class="twism-widget__notification__overlay" transition:fade />
    {/if}
    <div
      class="
        twism-widget__notification
        twism-widget__notification--{direction}
        twism-{containerStyle}
        {auth.provider}
      "
      style={containerStyle === 'popup' && getNotificationStyle()}
      transition:animate={{ containerStyle }}
    >
      <Toast />
      <svelte:component
        this={getFromMessage(
          messages[category],
          'component',
          messages[category]
        )}
      />
      {#if !getFromMessage(messages[category], 'hideArrow', false)}
        <span class="arrow-down" style={getArrowStyle()} />
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  @use './styles.scss' as *;
</style>
